import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Loader } from "@progress/kendo-react-indicators";
import { Input, MaskedTextBox, RadioGroup } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import CenterDivPanel from '../../components/CenterDivPanel';
import { EditableAddress } from '../../components/EditableAddress';
import useAlert from '../../components/useAlert';
import { fetchApi } from '../../services/api';
import { AddressViewModel } from '../../types/address';
import { IDName } from '../../types/idname';
import { isNullOrWhiteSpace } from '../../utils/utils';
import DriverAutoComplete from '../AssetDrivers/DriverAutoComplete';
import { DriverProfileType } from '../AssetDrivers/Profile';
import { OwnerType, OwnerTypes } from './Profile';
import { JsonResponse } from 'TypeGen/json-response';

type Props = {
    CloseDialog: () => void;
}

const AddOwnerDialog = (props: Props) => {
    const { alert } = useAlert();
    const [createType, setCreateType] = useState(1);
    const [copyFromDriverID, setCopyFromDriverID] = useState(0);
    // Copy
    const [copyOwnerNumber, setCopyOwnerNumber] = useState('');

    const [loading, setLoading] = useState<boolean>(false);
    const [owner, setOwner] = useState<OwnerType>(null);
    const [address, setAddress] = useState<AddressViewModel>({ AddressLine1: '', AddressLine2: '', City: '', State: '', ZipCode: '', Coordinates: { Latitude: 0, Longitude: 0 } });

    const history = useHistory();

    const hasAddress = address.AddressLine1.length > 0 && ((address.City.length > 0 && address.State.length > 0) || address.ZipCode.length > 0);
    const metNewRecordRequirements = createType === 1 && (isNullOrWhiteSpace(owner?.FirstName) === false &&
        isNullOrWhiteSpace(owner?.LastName) === false && hasAddress && owner?.OwnerType > 0);
    const metCopyRecordRequirements = createType === 0 && isNullOrWhiteSpace(copyOwnerNumber) == false && isNullOrWhiteSpace(owner?.OwnerNumber) == false;

    const canSave = metCopyRecordRequirements || metNewRecordRequirements;

    const _save = () => {
        if (canSave === false)
            return;

        setLoading(true);

        const data = {
            CopyOwnerNumber: createType === 0 ? copyOwnerNumber : null,
            ...owner,
            Address: address
        } as OwnerType

        fetchApi('/api/Asset/AddOwner', data, 'POST')
            .then((response: JsonResponse) => {
                setLoading(false);
                if (response.Success) {
                    const link = response.Links.find(l => l.Name === 'OwnerProfile');
                    if (link) history.push(link.Link);
                    props.CloseDialog();
                } else {
                    alert(response.ErrorMessage);
                }
            }
            ).catch((e) => {
                setLoading(false);
                // If not problem details
                if (!e?.status) alert('Unable to create owner');
            });
    }

    const copyFromDriverProfile = (driverId: number) => {
        setLoading(true);
        if (driverId > 0) {
            fetchApi(`/api/Asset/Driver/${driverId}`)
                .then((driverProfile: DriverProfileType) => {
                    setOwner({
                        ...owner,
                        FirstName: driverProfile.Driver.FirstName,
                        LastName: driverProfile.Driver.LastName,
                        EMailAddress: driverProfile.Driver.EMailAddress,
                        CellPhone1: driverProfile.Driver.CellPhone1
                    });
                    setAddress({
                        AddressLine1: driverProfile.Driver.Address.AddressLine1,
                        AddressLine2: driverProfile.Driver.Address.AddressLine2,
                        City: driverProfile.Driver.Address.City,
                        State: driverProfile.Driver.Address.State,
                        ZipCode: driverProfile.Driver.Address.ZipCode,
                        Coordinates: {
                            Latitude: driverProfile.Driver.Address.Coordinates.Latitude,
                            Longitude: driverProfile.Driver.Address.Coordinates.Longitude
                        }
                    });
                    setLoading(false);
                })
                .catch(async e => {
                    if (!e?.status)
                        await alert('Error: Please see admin');
                    else if (e.status !== 404) {
                        await alert(e?.detail);
                    }
                    setLoading(false);
                });
        }
        setCopyFromDriverID(driverId);
    }

    const options = [
        { label: "Copy Owner", value: 0 },
        { label: "Create Owner", value: 1 },
    ];

    return <div className="container-fluid">
        <Dialog title={`${createType === 0 ? 'Copy' : 'Create'} Owner Profile`} onClose={props.CloseDialog} width={400} height={createType === 0 ? 325 : 700}>
            {loading && <CenterDivPanel>
                <Loader type="converging-spinner" />
            </CenterDivPanel>}
            <div className="k-form k-form-md p-0">
                <div>
                    <RadioGroup
                        value={createType}
                        onChange={(e) => setCreateType(e.value)}
                        layout="horizontal"
                        data={options}
                    />
                    {createType === 0 && <FieldWrapper>
                        <Label>Copy Owner Number</Label>
                        <Input
                            required
                            maxLength={10}
                            disabled={loading}
                            value={copyOwnerNumber}
                            onChange={(e) => setCopyOwnerNumber(e.value)}
                        />
                    </FieldWrapper>}

                    {createType === 1 &&
                        <FieldWrapper>
                            <Label>Prefill Form w/Driver Info (optional)</Label>
                            <DriverAutoComplete
                                selectedDriverID={copyFromDriverID}
                                placeholder="Type a driver id"
                                onSelectedDriver={(e) => copyFromDriverProfile(e.DriverID)}
                            />
                        </FieldWrapper>
                    }

                    <FieldWrapper>
                        <Label>{`${createType === 0 ? 'New' : ''} Owner Number`}</Label>
                        <Input
                            required={createType === 0}
                            placeholder={`${createType === 0 ? '' : 'Leave blank to have it auto-generated'}`}
                            maxLength={10}
                            disabled={loading}
                            value={owner?.OwnerNumber}
                            onChange={(e) => setOwner({ ...owner, OwnerNumber: e.value })}
                        />
                    </FieldWrapper>
                    {createType === 1 && <div>
                        <FieldWrapper>
                            <Input
                                required
                                autoFocus
                                maxLength={40}
                                disabled={loading}
                                value={owner?.CompanyName}
                                label="Company Name"
                                onChange={(e) => setOwner({ ...owner, CompanyName: e.value })}
                            />
                        </FieldWrapper>
                        <FieldWrapper>
                            <Input
                                required
                                maxLength={20}
                                disabled={loading}
                                value={owner?.FirstName}
                                label="First Name"
                                onChange={(e) => setOwner({ ...owner, FirstName: e.value })}
                            />
                        </FieldWrapper>
                        <FieldWrapper>
                            <Input
                                required
                                maxLength={20}
                                disabled={loading}
                                value={owner?.LastName}
                                label="Last Name"
                                onChange={(e) => setOwner({ ...owner, LastName: e.value })}
                            />
                        </FieldWrapper>
                        <FieldWrapper>
                            <Input
                                type="email"
                                maxLength={50}
                                disabled={loading}
                                label="E-Mail"
                                value={owner?.EMailAddress}
                                onChange={(e) => setOwner({ ...owner, EMailAddress: e.value })}
                            />
                        </FieldWrapper>
                        <FieldWrapper>
                            <MaskedTextBox
                                mask="(000) 000-0000"
                                value={owner?.CellPhone1}
                                disabled={loading}
                                label="Cell Number"
                                onChange={(e) => setOwner({ ...owner, CellPhone1: e.value })}
                            />
                        </FieldWrapper>
                        <FieldWrapper>
                            <MaskedTextBox
                                mask="(000) 000-0000"
                                value={owner?.HomePhone1}
                                disabled={loading}
                                label="Home Number"
                                onChange={(e) => setOwner({ ...owner, HomePhone1: e.value })}
                            />
                        </FieldWrapper>
                        <FieldWrapper>
                            <Label>Owner Address</Label>
                            <EditableAddress
                                data={address}
                                title="Owner Address"
                                save={async (e) => setAddress(e)}
                            />
                        </FieldWrapper>
                        <FieldWrapper>
                            <DropDownList
                                required
                                disabled={loading}
                                data={OwnerTypes}
                                label="Owner Type"
                                textField="Name"
                                dataItemKey="ID"
                                value={OwnerTypes.find(x => x.ID == owner?.OwnerType)}
                                onChange={(e) => setOwner({ ...owner, OwnerType: (e.value as IDName).ID })}
                            />
                        </FieldWrapper>
                    </div>}
                </div>
            </div>

            <DialogActionsBar>
                <Button onClick={props.CloseDialog}>Cancel</Button>
                <Button themeColor="primary" disabled={canSave === false || loading} onClick={_save}>
                    {createType === 0 ? 'Copy' : 'Create'} Owner
                </Button>
            </DialogActionsBar>
        </Dialog >
    </div >
}

export default AddOwnerDialog;

